/**
 * Admin Sellers Management Page
 *
 * Complete overview of all seller companies with CRUD operations,
 * email domain management, and user management
 */

import { useState, useEffect } from 'react';
import {
  Building2,
  Search,
  MapPin,
  Mail,
  Globe,
  Users,
  Eye,
  MessageSquare,
  Loader2,
  AlertCircle,
  FlaskConical,
  Calendar,
  DollarSign,
  ExternalLink,
  X,
  Beaker,
  FileText,
  Download,
  Plus,
  Edit2,
  Trash2,
  Save,
  UserPlus,
  UserMinus,
  AtSign,
  Send,
  Clock,
  RefreshCw,
  CheckCircle,
  XCircle
} from 'lucide-react';
import api from '../../services/api';
import { supabase } from '../../lib/supabase';

// API base URL
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Helper to get auth token
const getAuthToken = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || null;
  } catch (err) {
    console.warn('Failed to get auth token:', err);
    return null;
  }
};

// Document type labels
const DOCUMENT_LABELS = {
  nda_template: 'NDA Template',
  corporate_deck: 'Corporate Presentation',
  teaser: 'Non-Confidential Teaser',
  investigator_brochure: "Investigator's Brochure",
  clinical_data_package: 'Clinical Data Package',
  patent_summary: 'Patent Portfolio Summary',
  cmc_overview: 'CMC Overview',
  regulatory_status: 'Regulatory Filing Status',
};

// Document download component
function AdminDocumentItem({ doc, docType }) {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (!doc?.filePath) return;

    setDownloading(true);
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(
        `${API_URL}/api/storage/signed-url?filePath=${encodeURIComponent(doc.filePath)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to get download URL');
      }

      const result = await response.json();
      window.open(result.data.signedUrl, '_blank');
    } catch (err) {
      console.error('Download error:', err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
      <div className="flex items-center gap-2 min-w-0">
        <FileText className="w-4 h-4 text-red-600 flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {DOCUMENT_LABELS[docType] || docType}
          </p>
          <p className="text-xs text-gray-500 truncate">{doc.fileName}</p>
        </div>
      </div>
      <button
        onClick={handleDownload}
        disabled={downloading}
        className="p-2 hover:bg-gray-200 rounded-lg disabled:opacity-50 flex-shrink-0"
        title="Download"
      >
        {downloading ? (
          <Loader2 className="w-4 h-4 animate-spin text-red-600" />
        ) : (
          <Download className="w-4 h-4 text-red-600" />
        )}
      </button>
    </div>
  );
}

export default function AdminSellers() {
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0 });

  // CRUD states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // User management states
  const [linkedUsers, setLinkedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [addingUser, setAddingUser] = useState(false);

  // Invitation states
  const [invitations, setInvitations] = useState([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);

  useEffect(() => {
    fetchSellers();
  }, [search]);

  const fetchSellers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/sellers', {
        params: { search, limit: pagination.limit, offset: pagination.offset }
      });
      if (response.data.success) {
        setSellers(response.data.data);
        setPagination(response.data.pagination);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSellerDetail = async (id) => {
    try {
      setDetailLoading(true);
      const response = await api.get(`/admin/sellers/${id}`);
      if (response.data.success) {
        setSelectedSeller(response.data.data);
        setEditData(response.data.data);
        fetchLinkedUsers(id);
        fetchInvitations(id);
      }
    } catch (err) {
      console.error('Failed to fetch seller details:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchLinkedUsers = async (sellerId) => {
    try {
      setLoadingUsers(true);
      const response = await api.get(`/admin/sellers/${sellerId}/users`);
      if (response.data.success) {
        setLinkedUsers(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch linked users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchInvitations = async (sellerId) => {
    try {
      setLoadingInvitations(true);
      const response = await api.get(`/admin/sellers/${sellerId}/invitations`);
      if (response.data.success) {
        setInvitations(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch invitations:', err);
    } finally {
      setLoadingInvitations(false);
    }
  };

  const handleSendInvite = async () => {
    if (!inviteEmail.trim()) return;
    setSendingInvite(true);
    try {
      await api.post('/admin/invitations', {
        email: inviteEmail,
        entity_type: 'seller',
        entity_id: selectedSeller.id
      });
      setInviteEmail('');
      fetchInvitations(selectedSeller.id);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSendingInvite(false);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    if (!confirm('Cancel this invitation?')) return;
    try {
      await api.delete(`/admin/invitations/${inviteId}`);
      fetchInvitations(selectedSeller.id);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleResendInvite = async (inviteId) => {
    try {
      await api.post(`/admin/invitations/${inviteId}/resend`);
      fetchInvitations(selectedSeller.id);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await api.post('/admin/sellers', editData);
      if (response.data.success) {
        setShowCreateModal(false);
        setEditData({});
        fetchSellers();
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      const response = await api.put(`/admin/sellers/${selectedSeller.id}`, editData);
      if (response.data.success) {
        setSelectedSeller(response.data.data);
        setEditMode(false);
        fetchSellers();
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Are you sure you want to delete "${selectedSeller.company_name}"? This will remove all associated data.`)) {
      return;
    }
    setDeleting(true);
    try {
      await api.delete(`/admin/sellers/${selectedSeller.id}`);
      setSelectedSeller(null);
      fetchSellers();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleAddUser = async () => {
    if (!newUserEmail.trim()) return;
    setAddingUser(true);
    try {
      await api.post(`/admin/sellers/${selectedSeller.id}/users`, { email: newUserEmail });
      setNewUserEmail('');
      fetchLinkedUsers(selectedSeller.id);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setAddingUser(false);
    }
  };

  const handleRemoveUser = async (userId, email) => {
    if (!confirm(`Remove ${email} from this seller?`)) return;
    try {
      await api.delete(`/admin/sellers/${selectedSeller.id}/users/${userId}`);
      fetchLinkedUsers(selectedSeller.id);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStageColor = (stage) => {
    const colors = {
      'Preclinical': 'bg-gray-100 text-gray-700',
      'Phase 1': 'bg-blue-100 text-blue-700',
      'Phase 2': 'bg-yellow-100 text-yellow-700',
      'Phase 3': 'bg-orange-100 text-orange-700',
      'Approved': 'bg-green-100 text-green-700',
      'Discovery': 'bg-purple-100 text-purple-700'
    };
    return colors[stage] || 'bg-gray-100 text-gray-700';
  };

  if (loading && sellers.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Seller Companies</h1>
          <p className="text-gray-600 mt-1">
            {pagination.total} biotech/pharma companies registered
          </p>
        </div>
        <button
          onClick={() => { setEditData({}); setShowCreateModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          <Plus className="w-4 h-4" />
          Add Seller
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search by company name, email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
        />
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <p className="text-red-800">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Sellers Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {sellers.map((seller) => (
          <div
            key={seller.id}
            className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => fetchSellerDetail(seller.id)}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                {seller.logo_url ? (
                  <img
                    src={seller.logo_url}
                    alt={seller.company_name}
                    className="w-12 h-12 rounded-lg object-contain bg-gray-50"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-white" />
                  </div>
                )}
                <div>
                  <h3 className="font-semibold text-gray-900">{seller.company_name}</h3>
                  {seller.company_name_cn && (
                    <p className="text-sm text-gray-500">{seller.company_name_cn}</p>
                  )}
                </div>
              </div>
              {seller.stage && (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStageColor(seller.stage)}`}>
                  {seller.stage}
                </span>
              )}
            </div>

            <div className="space-y-2 text-sm">
              {seller.headquarters && (
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin className="w-4 h-4" />
                  <span>{seller.headquarters}</span>
                </div>
              )}
              {seller.user_email && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Mail className="w-4 h-4" />
                  <span>{seller.user_email}</span>
                </div>
              )}
              {seller.verified_email_domains && seller.verified_email_domains.length > 0 && (
                <div className="flex items-center gap-2 text-gray-600">
                  <AtSign className="w-4 h-4" />
                  <span className="text-purple-600">{seller.verified_email_domains.join(', ')}</span>
                </div>
              )}
              {seller.website && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Globe className="w-4 h-4" />
                  <a
                    href={seller.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline truncate"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {seller.website.replace(/^https?:\/\//, '')}
                  </a>
                </div>
              )}
            </div>

            {/* Therapeutic Areas */}
            {seller.therapeutic_areas && seller.therapeutic_areas.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {seller.therapeutic_areas.slice(0, 3).map((area, i) => (
                  <span key={i} className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded text-xs">
                    {area}
                  </span>
                ))}
                {seller.therapeutic_areas.length > 3 && (
                  <span className="text-xs text-gray-500">+{seller.therapeutic_areas.length - 3} more</span>
                )}
              </div>
            )}

            {/* Stats Row */}
            <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-6 text-sm">
              <div className="flex items-center gap-1.5 text-gray-600">
                <Eye className="w-4 h-4" />
                <span>{seller.total_views || 0} views</span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-600">
                <Users className="w-4 h-4" />
                <span>{seller.unique_buyers || 0} buyers</span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-600">
                <MessageSquare className="w-4 h-4" />
                <span>{seller.intro_requests || 0} intros</span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-600">
                <Beaker className="w-4 h-4" />
                <span>{seller.asset_count || 0} assets</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {sellers.length === 0 && !loading && (
        <div className="text-center py-12 text-gray-500">
          No sellers found matching your search.
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Add New Seller</h2>
              <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-150px)]">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                  <input
                    type="text"
                    required
                    value={editData.company_name || ''}
                    onChange={(e) => setEditData({ ...editData, company_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Chinese Name</label>
                  <input
                    type="text"
                    value={editData.company_name_cn || ''}
                    onChange={(e) => setEditData({ ...editData, company_name_cn: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Headquarters</label>
                  <input
                    type="text"
                    value={editData.headquarters || ''}
                    onChange={(e) => setEditData({ ...editData, headquarters: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                  <input
                    type="text"
                    value={editData.country || ''}
                    onChange={(e) => setEditData({ ...editData, country: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Domains (comma-separated, e.g., company.com, company.cn)
                </label>
                <input
                  type="text"
                  value={editData.verified_email_domains?.join(', ') || ''}
                  onChange={(e) => setEditData({
                    ...editData,
                    verified_email_domains: e.target.value.split(',').map(d => d.trim()).filter(d => d)
                  })}
                  placeholder="company.com, company.cn"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                />
                <p className="text-xs text-gray-500 mt-1">Users registering with these domains will auto-link to this seller</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                <input
                  type="url"
                  value={editData.website || ''}
                  onChange={(e) => setEditData({ ...editData, website: e.target.value })}
                  placeholder="https://company.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  rows={3}
                  value={editData.description || ''}
                  onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Create Seller
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedSeller && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-4">
                {selectedSeller.logo_url ? (
                  <img
                    src={selectedSeller.logo_url}
                    alt={selectedSeller.company_name}
                    className="w-14 h-14 rounded-xl object-contain bg-gray-50"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
                    <Building2 className="w-7 h-7 text-white" />
                  </div>
                )}
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{selectedSeller.company_name}</h2>
                  {selectedSeller.company_name_cn && (
                    <p className="text-gray-500">{selectedSeller.company_name_cn}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!editMode ? (
                  <>
                    <button
                      onClick={() => setEditMode(true)}
                      className="p-2 hover:bg-gray-100 rounded-lg text-blue-600"
                      title="Edit"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={deleting}
                      className="p-2 hover:bg-red-50 rounded-lg text-red-600"
                      title="Delete"
                    >
                      {deleting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Trash2 className="w-5 h-5" />}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleUpdate}
                      disabled={saving}
                      className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Save
                    </button>
                    <button
                      onClick={() => { setEditMode(false); setEditData(selectedSeller); }}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </>
                )}
                <button
                  onClick={() => { setSelectedSeller(null); setEditMode(false); }}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
              {detailLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-red-600" />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Editable Fields */}
                  {editMode ? (
                    <div className="space-y-4 bg-yellow-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-yellow-900">Edit Mode</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                          <input
                            type="text"
                            value={editData.company_name || ''}
                            onChange={(e) => setEditData({ ...editData, company_name: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Chinese Name</label>
                          <input
                            type="text"
                            value={editData.company_name_cn || ''}
                            onChange={(e) => setEditData({ ...editData, company_name_cn: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Headquarters</label>
                          <input
                            type="text"
                            value={editData.headquarters || ''}
                            onChange={(e) => setEditData({ ...editData, headquarters: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                          <input
                            type="text"
                            value={editData.country || ''}
                            onChange={(e) => setEditData({ ...editData, country: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email Domains (auto-link users)
                        </label>
                        <input
                          type="text"
                          value={editData.verified_email_domains?.join(', ') || ''}
                          onChange={(e) => setEditData({
                            ...editData,
                            verified_email_domains: e.target.value.split(',').map(d => d.trim()).filter(d => d)
                          })}
                          placeholder="company.com, company.cn"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                          <input
                            type="url"
                            value={editData.website || ''}
                            onChange={(e) => setEditData({ ...editData, website: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Stage</label>
                          <select
                            value={editData.stage || ''}
                            onChange={(e) => setEditData({ ...editData, stage: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          >
                            <option value="">Select stage...</option>
                            <option value="Discovery">Discovery</option>
                            <option value="Preclinical">Preclinical</option>
                            <option value="Phase 1">Phase 1</option>
                            <option value="Phase 2">Phase 2</option>
                            <option value="Phase 3">Phase 3</option>
                            <option value="Approved">Approved</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                          rows={3}
                          value={editData.description || ''}
                          onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Basic Info */}
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <div className="text-sm text-gray-500 mb-1">Location</div>
                          <div className="font-medium flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-gray-400" />
                            {selectedSeller.headquarters || 'N/A'}
                          </div>
                        </div>
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <div className="text-sm text-gray-500 mb-1">Founded</div>
                          <div className="font-medium flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            {selectedSeller.founded || 'N/A'}
                          </div>
                        </div>
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <div className="text-sm text-gray-500 mb-1">Stage</div>
                          <div className="font-medium">
                            {selectedSeller.stage && (
                              <span className={`px-2 py-1 rounded text-sm ${getStageColor(selectedSeller.stage)}`}>
                                {selectedSeller.stage}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Email Domains */}
                      {selectedSeller.verified_email_domains && selectedSeller.verified_email_domains.length > 0 && (
                        <div className="bg-purple-50 p-4 rounded-lg">
                          <h3 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
                            <AtSign className="w-5 h-5" />
                            Verified Email Domains
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {selectedSeller.verified_email_domains.map((domain, i) => (
                              <span key={i} className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                                @{domain}
                              </span>
                            ))}
                          </div>
                          <p className="text-xs text-purple-700 mt-2">Users with these email domains auto-link to this seller</p>
                        </div>
                      )}

                      {/* Contact Info */}
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h3 className="font-semibold text-blue-900 mb-3">Contact Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-blue-600" />
                            <span className="text-blue-800">{selectedSeller.user_email || 'No registered user'}</span>
                          </div>
                          {selectedSeller.website && (
                            <div className="flex items-center gap-2">
                              <Globe className="w-4 h-4 text-blue-600" />
                              <a
                                href={selectedSeller.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-800 hover:underline flex items-center gap-1"
                              >
                                {selectedSeller.website.replace(/^https?:\/\//, '')}
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Linked Users Section */}
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Linked User Accounts ({linkedUsers.length})
                    </h3>

                    {loadingUsers ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin text-green-600" />
                      </div>
                    ) : (
                      <>
                        {/* Add User */}
                        <div className="flex gap-2 mb-3">
                          <input
                            type="email"
                            value={newUserEmail}
                            onChange={(e) => setNewUserEmail(e.target.value)}
                            placeholder="user@company.com"
                            className="flex-1 px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500"
                          />
                          <button
                            onClick={handleAddUser}
                            disabled={addingUser || !newUserEmail.trim()}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                          >
                            {addingUser ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                            Add
                          </button>
                        </div>

                        {/* User List */}
                        {linkedUsers.length > 0 ? (
                          <div className="space-y-2">
                            {linkedUsers.map((user) => (
                              <div key={user.id} className="flex items-center justify-between p-2 bg-white rounded-lg">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                                    <span className="text-sm font-medium text-green-700">
                                      {user.email.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">{user.email}</p>
                                    <p className="text-xs text-gray-500">
                                      Joined {formatDate(user.created_at)}
                                      {user.subscription_tier && ` • ${user.subscription_tier}`}
                                    </p>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleRemoveUser(user.id, user.email)}
                                  className="p-1.5 hover:bg-red-50 rounded text-red-600"
                                  title="Remove user"
                                >
                                  <UserMinus className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-green-700">No users linked to this seller yet.</p>
                        )}
                      </>
                    )}
                  </div>

                  {/* Invitations Section */}
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                      <Send className="w-5 h-5" />
                      Invite New Users
                    </h3>

                    {/* Send Invitation */}
                    <div className="flex gap-2 mb-3">
                      <input
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="Enter email to invite..."
                        className="flex-1 px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        onClick={handleSendInvite}
                        disabled={sendingInvite || !inviteEmail.trim()}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                      >
                        {sendingInvite ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Invite
                      </button>
                    </div>
                    <p className="text-xs text-blue-700 mb-3">User will receive an email with a registration link that auto-links them to this seller.</p>

                    {/* Pending Invitations */}
                    {loadingInvitations ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                      </div>
                    ) : invitations.length > 0 ? (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-blue-900">Pending Invitations</p>
                        {invitations.filter(inv => inv.status === 'pending').map((inv) => (
                          <div key={inv.id} className="flex items-center justify-between p-2 bg-white rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                <Clock className="w-4 h-4 text-blue-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{inv.email}</p>
                                <p className="text-xs text-gray-500">
                                  Sent {formatDate(inv.created_at)}
                                  {new Date(inv.expires_at) < new Date() ? (
                                    <span className="text-red-600 ml-1">(Expired)</span>
                                  ) : (
                                    <span className="text-blue-600 ml-1">• Expires {formatDate(inv.expires_at)}</span>
                                  )}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <button
                                onClick={() => handleResendInvite(inv.id)}
                                className="p-1.5 hover:bg-blue-100 rounded text-blue-600"
                                title="Resend invitation"
                              >
                                <RefreshCw className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleCancelInvite(inv.id)}
                                className="p-1.5 hover:bg-red-50 rounded text-red-600"
                                title="Cancel invitation"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                        {invitations.filter(inv => inv.status === 'accepted').length > 0 && (
                          <>
                            <p className="text-sm font-medium text-green-900 mt-3">Accepted Invitations</p>
                            {invitations.filter(inv => inv.status === 'accepted').map((inv) => (
                              <div key={inv.id} className="flex items-center gap-3 p-2 bg-green-50 rounded-lg">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{inv.email}</p>
                                  <p className="text-xs text-gray-500">Accepted</p>
                                </div>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-blue-700">No pending invitations.</p>
                    )}
                  </div>

                  {/* Description */}
                  {!editMode && selectedSeller.description && (
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">About</h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{selectedSeller.description}</p>
                    </div>
                  )}

                  {/* Therapeutic Areas & Tech */}
                  {!editMode && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedSeller.therapeutic_areas && selectedSeller.therapeutic_areas.length > 0 && (
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-2">Therapeutic Areas</h3>
                          <div className="flex flex-wrap gap-2">
                            {selectedSeller.therapeutic_areas.map((area, i) => (
                              <span key={i} className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                                {area}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {selectedSeller.tech_platforms && selectedSeller.tech_platforms.length > 0 && (
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-2">Technology Platforms</h3>
                          <div className="flex flex-wrap gap-2">
                            {selectedSeller.tech_platforms.map((tech, i) => (
                              <span key={i} className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                                {tech}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Pipeline Assets */}
                  {!editMode && selectedSeller.assets && selectedSeller.assets.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3">Pipeline Assets ({selectedSeller.assets.length})</h3>
                      <div className="space-y-2">
                        {selectedSeller.assets.map((asset) => (
                          <div key={asset.id} className="p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                            <div>
                              <div className="font-medium">{asset.asset_name}</div>
                              <div className="text-sm text-gray-500">{asset.indication}</div>
                            </div>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getStageColor(asset.stage)}`}>
                              {asset.stage}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Uploaded Documents */}
                  {!editMode && selectedSeller.documents && Object.keys(selectedSeller.documents).length > 0 && (
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-red-600" />
                        Uploaded Documents ({Object.keys(selectedSeller.documents).length})
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {Object.entries(selectedSeller.documents).map(([docType, doc]) => (
                          <AdminDocumentItem key={docType} docType={docType} doc={doc} />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Engagement Stats */}
                  {!editMode && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-gray-900">{selectedSeller.total_views || 0}</div>
                        <div className="text-sm text-gray-500">Profile Views</div>
                      </div>
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-gray-900">{selectedSeller.unique_buyers || 0}</div>
                        <div className="text-sm text-gray-500">Unique Buyers</div>
                      </div>
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-gray-900">{selectedSeller.introductionRequests?.length || 0}</div>
                        <div className="text-sm text-gray-500">Intro Requests</div>
                      </div>
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-gray-900">{selectedSeller.subscription_tier || 'Free'}</div>
                        <div className="text-sm text-gray-500">Subscription</div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
